<template>
    <div>
        <data-table
            v-if="tableLoaded"
            :exportable="this.$store.getters.hasPermission(['project_current_welds'], ['read']) && archived == 1" 
            title=""
            :rows="getData.rowData"
            :columns="columnData"
            :perPage="perPage"
            :rowsCount="rowsCount"
            :currentPage="currentPage"
            :pageCount="pageCount"
            :rowChangeSelectOptions="rowChangeSelectOptions"
            :disabledInputs="!this.$store.getters.hasPermission(['project_ndt_welds'], ['update'])"
            @changePage="changePage"
            @searchEvent="searchEvent"
            @orderByEvent="orderByEvent"
            @rowClickDelete="onDeleteRow"
            @rowChangeTextInput="onEditRow"
            @rowChangeDate="onEditRow"
            @rowChangeSelect="onEditRow"
            @rowChangeTextAreaInput="onEditRow"
            @rowChangeBooleanInput="onEditRow"
            @rowClickCheck="onClickCheck"
            @rowClickAddFiles="onAddFiles"
            @exportButtonClick="onExportButton"
        >
        </data-table>

        <b-btn
            v-if="this.$store.getters.hasPermission(['project_current_welds'], ['read'])"
            class="btn btn-dark mt-3" 
            @click="goToCurrentWelds" 
        >
            <i class="fa fa-arrow-left" aria-hidden="true"></i> {{$t('general.buttons.go_to.go_to_current_welds')}}
        </b-btn>

        <confirm-dialog ref="deleteModal" :title="actionTitle" @buttonClick="deleteAction">
            {{ $t('messages.delete') }}
        </confirm-dialog>

        <add-files-modal
            ref="addFilesModal"
            :fileType="'weld_request'"
            :filterBy="'weld_request_id'"
            :canUploadFiles="(archived != 1)"
        >
        </add-files-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import http from '@/libs/http';
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import miniToastr from 'mini-toastr';
import AddFilesModal from '@/components/global/modals/AddFilesModal.vue';

export default {
    name: 'ndt_welds_table',
    components: {
        DataTable,
        ConfirmDialog,
        AddFilesModal,
    },
    props: {
        tableName: {
            type: String,
            required: true,
        },
        archived: {
            type: Number,
            required: true,
        },
        rowChangeSelectOptions: {
            type: Object,
            default () {
                return null;
            },
        },
        columnData: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            tableNameTranslated: Vue.i18n.translate('general.welding.ndt_welds.title_modal'),
            actionTitle: '',

            row: null,
            tableLoaded: false,
            perPage: 0,
            rowsCount: 0,
            pageCount: 1,
            currentPage: 1,
            sortType: 'asc',
            sortBy: 'id',
            searchInput: '',

            getData: {
                rowData: [],
            },
        }
    },
    mounted() {
        if (this.archived == 0) {
            if (this.$store.state.key == 'redirected_from_current_welds') {
                this.redirectedFromCurrentWelds();
            }

            this.$store.state.key = null;

            this.onLoadTable();
        }
    },
    computed: {
        current_weld_special_columns: {
            get: function () {
                let current_weld_special_columns = this.$store.getters.getCurrentWeldSpecialColumns;

                return current_weld_special_columns;
            }
        }
    },
    watch: {
        current_weld_special_columns: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        }
    },
    methods: {
        successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
        orderByEvent (column, sort_type) {
            this.sortType = sort_type;
            this.sortBy = column.field;
            this.onLoadTable();
        },
        searchEvent (searchInput) {
            this.searchInput = searchInput;
            this.onLoadTable();
        },
        changePage (page) {
            this.currentPage = page;
            this.onLoadTable();
        },
        onLoadTable () {
            http.route(this.tableName + '?project_id=' + this.$route.params.id + '&filters[requested]=1&filters[closed_or_deleted]=' + this.archived + '&search=' + this.searchInput + '&page=' + this.currentPage + '&sort_by=' + this.sortBy + '&sort_order=' + this.sortType).get().then(response => {
                if (!response.isError) {
                    this.perPage     = response.pagination.limit;
                    this.rowsCount   = response.pagination.totalEntries;
                    this.currentPage = response.pagination.currentPage;
                    this.pageCount   = response.pagination.totalPages;
                    var data = response.data;
                    data.forEach((field, index) => {
                        if (
                            typeof this.current_weld_special_columns != 'undefined' &&
                            typeof this.current_weld_special_columns[field.testing_type] != 'undefined'
                        ) {
                            field.testing_typeV = this.current_weld_special_columns[field.testing_type].name;
                        }

                        if (this.archived == 0) {
                            field.repair = Boolean(field.repair);
                        } else {
                            field.repair = (field.repair == 1) ? Vue.i18n.translate('misc.yes') : Vue.i18n.translate('misc.no') ;
                        }

                        if (field.weld.spool == null) {
                            field.weld.spool = { 
                                'spool_no': '' 
                            };
                        }

                        if (field.weld.project_setting == null) {
                            field.weld.project_setting = { 
                                'value': '' 
                            };
                        }

                        if (field.weld.weld_material == null) {
                            field.weld.weld_material = { 
                                'value': '' 
                            };
                        }

                        if (field.weld.welder1 == null) {
                            field.weld.welder1 = { 
                                'fullname': '' 
                            };
                        }

                        if (field.weld.welder2 == null) {
                            field.weld.welder2 = { 
                                'fullname': '' 
                            };
                        }

                        if (field.company == null) {
                            field.company = { 
                                'name': '' 
                            };
                        }

                        if (field.repair_reason == null) {
                            field.repair_reason = { 
                                'value': '' 
                            };
                        }

                        if (field.weld.inspection == null) {
                            field.weld.inspection = { 
                                'value': '' 
                            };
                        }

                        if (field.weld_request_overview == null) {
                            field.weld_request_overview = { 
                                'ndt_request_no': '' 
                            };
                        }
                    });

                    this.getData.rowData = data;
                    this.tableLoaded = true;
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        onEditRow (row, index, column, column_name, value) {
            let put_data = {};

            put_data[column_name] = value;

            this.updateRow(row, put_data);
        },
        onClickCheck (row, index, column) {
            let put_data = {};

            put_data['project_id'] = this.$route.params.id;
            put_data[column] = !row[column.toString()];

            Object.keys(put_data).forEach(key => {
                if (put_data[key] === '') {
                    put_data[key] = null;
                }
            });

            http.route('weld_request/' + row.id).patch(put_data, true)
            .then(response => {
                if (!response.isError) {
                    row[column.toString()] = !row[column.toString()];
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        updateRow (row, put_data, deleted = false) {
            put_data['project_id'] = this.$route.params.id;

            Object.keys(put_data).forEach(key => {
                if (put_data[key] === '') {
                    put_data[key] = null;
                }
            });

            http.route('weld_request/' + row.id).patch(put_data, true)
            .then(response => {
                if (!response.isError) {
                    if (deleted) {
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.deleted'));
                    } else {
                        this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.updated'));
                    }
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }
            });
        },
        onDeleteRow (row, index) {
            this.actionTitle = Vue.i18n.translate('modal.general.delete') + ' ' + this.tableNameTranslated;
            this.row = row;
            this.$refs.deleteModal.show();
        },
        deleteAction (button) {
            if (button == 1) {
                let put_data = {};
                put_data['deleted'] = 1;

                this.updateRow(this.row, put_data, true);
            }

            this.$refs.deleteModal.hide();
        },
        onAddFiles (row) {
            this.$refs.addFilesModal.showModal(row);
        },
        goToCurrentWelds () {
            this.$router.push({name: 'current_welds'});
        },
        redirectedFromCurrentWelds () {
            this.successMsg(Vue.i18n.translate('messages.request_made'));
        },
        onExportButton() {
            http.route(this.tableName + '?project_id=' + this.$route.params.id + '&filters[requested]=1&filters[closed_or_deleted]=' + this.archived).exportExcel({
                export: 1,
                search: this.searchInput,
                sort_by: this.sortBy,
                sort_order: this.sortType
            }, 'ndt_welds.xlsx').then(response => {
                if (typeof response != 'undefined') {
                    this.errorMsg(Vue.i18n.translate('errors.error_export'));
                } else {
                    this.successMsg(this.tableNameTranslated + Vue.i18n.translate('messages.exported'));
                }
            });
        },
    }
}
</script>


// WEBPACK FOOTER //
// src/components/pages/project/welding/ndt_welds/ndt_welds_table.vue